export interface RefreshCoordinatorOptions {
  currentRefresh: () => string | null;
  withCrossTabLock: <T>(task: () => Promise<T>) => Promise<T>;
  rotate: (refresh: string) => Promise<'refreshed' | 'invalid'>;
  invalidate: (refresh: string) => void;
}

/** Coordinates rotating refresh tokens within one runtime and across tabs. */
export function createRefreshCoordinator(options: RefreshCoordinatorOptions):
  (expectedRefresh: string | null) => Promise<boolean> {
  const inFlight = new Map<string, Promise<boolean>>();

  return (expectedRefresh: string | null): Promise<boolean> => {
    if (!expectedRefresh) return Promise.resolve(false);
    const existing = inFlight.get(expectedRefresh);
    if (existing) return existing;

    const pending = options.withCrossTabLock(async (): Promise<boolean> => {
        const current = options.currentRefresh();
        if (!current) return false;
        // Another tab already rotated the token used by the failed request.
        if (current !== expectedRefresh) return true;
        const result = await options.rotate(current);
        if (result === 'refreshed') return true;
        // Invalidate only while the rejected token is still authoritative. A
        // cooperative tab cannot rotate it under this lock, and the equality
        // check protects against an external/non-cooperating token update.
        if (options.currentRefresh() === current) options.invalidate(current);
        return false;
      }).finally(() => { inFlight.delete(expectedRefresh); });
    inFlight.set(expectedRefresh, pending);
    return pending;
  };
}

export interface StorageLeaseLockOptions {
  key?: string;
  owner?: string;
  leaseMs?: number;
  acquireTimeoutMs?: number;
  settleMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

type LeaseRecord = { owner: string; expiresAt: number };

/** A Web-Locks fallback for browsers that share localStorage but do not expose
 * navigator.locks. The short settle window arbitrates simultaneous claims;
 * the refresh request itself is bounded to finish before the lease expires. */
export function createStorageLeaseLock(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  options: StorageLeaseLockOptions = {},
): <T>(task: () => Promise<T>) => Promise<T> {
  const key = options.key ?? 'piixel-auth-refresh-lease';
  const owner = options.owner ?? crypto.randomUUID();
  const leaseMs = options.leaseMs ?? 20_000;
  const acquireTimeoutMs = options.acquireTimeoutMs ?? 25_000;
  const settleMs = options.settleMs ?? 40;
  const now = options.now ?? Date.now;
  const wait = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  const read = (): LeaseRecord | null => {
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      const value = JSON.parse(raw) as Partial<LeaseRecord>;
      return typeof value.owner === 'string' && typeof value.expiresAt === 'number'
        ? { owner: value.owner, expiresAt: value.expiresAt }
        : null;
    } catch { return null; }
  };

  return async <T>(task: () => Promise<T>): Promise<T> => {
    const deadline = now() + acquireTimeoutMs;
    for (;;) {
      const current = read();
      if (!current || current.expiresAt <= now()) {
        storage.setItem(key, JSON.stringify({ owner, expiresAt: now() + leaseMs } satisfies LeaseRecord));
        // Let another tab with a simultaneous claim write before deciding who won.
        await wait(settleMs);
        const confirmed = read();
        if (confirmed?.owner === owner && confirmed.expiresAt > now()) {
          try {
            return await task();
          } finally {
            if (read()?.owner === owner) storage.removeItem(key);
          }
        }
      }
      if (now() >= deadline) throw new Error('Timed out waiting to refresh the Piixel session. Please try again.');
      const remainingLease = Math.max(20, Math.min(120, (current?.expiresAt ?? now()) - now()));
      await wait(remainingLease);
    }
  };
}
